import { Linkedin, Twitter, Instagram, Youtube } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND_LOGOS, FOOTER_COLUMNS, SOCIAL_LINKS } from "@/lib/site";

const SOCIAL_ICONS = { linkedin: Linkedin, twitter: Twitter, instagram: Instagram, youtube: Youtube };

export default function Footer() {
  return (
    <footer className="relative pt-16 pb-10 border-t border-white/[0.05]" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative shrink-0">
                <div className="absolute inset-[-25%] pointer-events-none"
                  style={{ background: "radial-gradient(closest-side, rgba(96,165,250,0.22), rgba(37,99,235,0.08) 55%, transparent 80%)", filter: "blur(8px)" }} />
                <img
                  src={BRAND_LOGOS.navbar}
                  alt="Esthera RealityOS"
                  className="relative w-11 h-11 object-contain transition-transform duration-500 group-hover:scale-[1.06]"
                  style={{ mixBlendMode: "screen", filter: "drop-shadow(0 0 5px rgba(96,165,250,0.4)) brightness(1.05) contrast(1.03)" }}
                  draggable="false"
                />
              </div>
              <div className="leading-[1.0]">
                <div className="font-display text-[18px] tracking-[0.22em] text-platinum font-semibold">ESTHERA</div>
                <div className="text-[10px] tracking-[0.55em] text-sapphire-glow font-semibold pl-[0.55em] mt-[4px]"
                  style={{ textShadow: "0 0 6px rgba(96,165,250,0.5)" }}>REALITYOS</div>
              </div>
            </Link>
            <div className="mt-5 text-[13px] text-platinum/55 leading-[1.65] max-w-[220px]">
              The AI Operating System for Real Estate.
            </div>
            <div className="mt-6 flex items-center gap-3">
              {SOCIAL_LINKS.map((s, i) => {
                const Icon = SOCIAL_ICONS[s.key];
                const baseClass = "w-9 h-9 rounded-md inline-flex items-center justify-center transition-colors";
                if (s.active) {
                  return (
                    <a
                      key={s.key}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`footer-social-${s.key}`}
                      className={`${baseClass} border border-sapphire/40 text-sapphire hover:text-sapphire-glow hover:border-sapphire-glow`}
                      style={{ background: "rgba(37,99,235,0.08)" }}
                      aria-label={s.label}
                      title={s.label}
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  );
                }
                return (
                  <span
                    key={s.key}
                    data-testid={`footer-social-${s.key}`}
                    className={`${baseClass} border border-white/[0.06] text-platinum/25 cursor-not-allowed`}
                    aria-label={s.label}
                    title={s.label}
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                );
              })}
            </div>
          </div>

          {/* Columns */}
          {FOOTER_COLUMNS.map((col, ci) => (
            <div key={col.title} data-testid={`footer-col-${ci}`}>
              <div className="eyebrow text-platinum/65" style={{ color: "#94A3B8" }}>{col.title}</div>
              <ul className="mt-4 space-y-2.5">
                {col.items.map((item) => {
                  const isHash = item.href.includes("#");
                  const Cmp = isHash ? "a" : Link;
                  const props = isHash ? { href: item.href } : { to: item.href };
                  return (
                    <li key={item.label}>
                      <Cmp
                        {...props}
                        data-testid={`footer-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                        className="text-[13.5px] text-platinum/65 hover:text-white transition-colors"
                      >
                        {item.label}
                      </Cmp>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="hr-platinum my-10" />

        <div className="flex flex-col lg:flex-row items-center justify-between gap-5">
          <div className="text-[12px] tracking-[0.22em] text-sapphire-glow uppercase">
            AI THAT WORKS. INTELLIGENCE THAT GROWS.
          </div>
          <div className="flex items-center gap-6 text-[12px] text-platinum/45">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms"   data-testid="footer-terms-link"   className="hover:text-white">Terms of Service</Link>
            <span>
              © 2026 Esthera RealityOS. All rights reserved.
              {/* Subtle founder entry — sapphire dot at the end of the copyright */}
              <Link
                to="/admin/leads"
                data-testid="footer-admin-entry"
                aria-label="Admin"
                title="Admin"
                className="inline-block ml-1.5 w-[6px] h-[6px] rounded-full align-middle bg-sapphire-glow/55 hover:bg-sapphire-glow transition-all hover:scale-[1.6]"
                style={{ boxShadow: "0 0 6px rgba(96,165,250,0.65)" }}
              />
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
